/**
 * CompanyRegistrationTrackPage — list of every company registration
 * request with a filter (قيد الإجراء / مكتمل) and a debounced search
 * box. Each row navigates to the detail page.
 *
 * (iter-220, Feb 2026)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Search, User, Hash, Building2, Calendar } from 'lucide-react';
import { Input } from '../components/ui/input';
import { useLanguage } from '../context/LanguageContext';
import { AgentPageShell } from './agent/AgentPageShell';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const token = sessionStorage.getItem('admin_token') || localStorage.getItem('admin_token');
  return { Authorization: 'Bearer ' + token };
};

const STATUS_TABS = [
  { key: 'all', ar: 'الكل', en: 'All' },
  { key: 'in_progress', ar: 'قيد الإجراء', en: 'In progress' },
  { key: 'completed', ar: 'مكتمل', en: 'Completed' },
];

// iter-261 (Feb 2026) — date filter presets for the top toolbar.
const YEAR_OPTIONS = (() => {
  const now = new Date().getFullYear();
  return ['all', String(now), String(now - 1), String(now - 2)];
})();

// Format a request-date (YYYY-MM-DD or ISO) into a compact display string.
function fmtDate(v, isAr) {
  if (!v) return '';
  try {
    // Only take the first 10 chars if it's ISO — the request_date is a
    // pure calendar date the operator selected, no need to render TZ shifts.
    const d = String(v).length >= 10 ? String(v).slice(0, 10) : v;
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString(isAr ? 'ar-EG' : 'en-GB');
  } catch { return String(v); }
}

export default function CompanyRegistrationTrackPage() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  // iter-261 — date filters (from/to + quick year picker).
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [year, setYear] = useState('all');

  const load = useCallback(async (statusFilter, query, from, to) => {
    setLoading(true);
    try {
      const params = { page: 1, limit: 200 };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (query && query.trim()) params.q = query.trim();
      if (from) params.date_from = from;
      if (to)   params.date_to   = to;
      const res = await axios.get(API + '/api/admin/company-registrations', { headers: authHeader(), params });
      setItems((res.data && res.data.data) || []);
      setTotal((res.data && res.data.total) || 0);
    } catch {
      toast.error(t('فشل التحميل', 'Failed to load'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAr]);

  // Initial + reload when tab / date filters change
  useEffect(() => { load(tab, q, dateFrom, dateTo); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab, dateFrom, dateTo]);

  // Debounced search
  useEffect(() => {
    const h = setTimeout(() => load(tab, q, dateFrom, dateTo), 350);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // iter-261 — Year quick-picker updates from/to bounds.
  const applyYear = (y) => {
    setYear(y);
    if (y === 'all') { setDateFrom(''); setDateTo(''); return; }
    setDateFrom(`${y}-01-01`);
    setDateTo(`${y}-12-31`);
  };

  const breadcrumb = [
    { label_ar: 'الرئيسية', label_en: 'Home', path: '/dashboard' },
    { label_ar: 'Cloud GM Company Agent', label_en: 'Cloud GM Company Agent', path: '/manage/cloudgm-agent' },
    { label_ar: 'تسجيل الشركات', label_en: 'Company Registration', path: '/manage/cloudgm-agent/company-registration' },
    { label_ar: 'متابعة تسجيل الشركات', label_en: 'Track Registrations', path: null },
  ];

  return (
    <AgentPageShell breadcrumb={breadcrumb} onBack={() => navigate('/manage/cloudgm-agent/company-registration')}>
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white mb-1">
            {t('متابعة تسجيل الشركات', 'Track Registrations')}
          </h1>
          <p className="text-sm text-[#71717A]">
            {t('عرض جميع طلبات تسجيل الشركات', 'View every company registration request')}
          </p>
        </div>

        {/* Tabs + Search */}
        <div className="bg-white/[0.02] rounded-2xl p-4 border border-white/10 mb-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex gap-2 flex-wrap">
            {STATUS_TABS.map((s) => (
              <button
                key={s.key}
                onClick={() => setTab(s.key)}
                className={
                  'px-4 py-2 rounded-xl text-sm font-medium transition-colors ' +
                  (tab === s.key
                    ? 'bg-emerald-500 text-white'
                    : 'bg-[#0a0a0a] text-[#71717A] hover:bg-[#151530]')
                }
                data-testid={'tab-' + s.key}
              >
                {isAr ? s.ar : s.en}
              </button>
            ))}
          </div>
          <div className="flex-1 relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#606060]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('ابحث برقم الطلب أو اسم العميل...', 'Search by request code or client name...')}
              className="bg-[#0a0a0a] border-[#2a2a4e] text-white pr-10 rounded-xl"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* iter-261 — Date filters (from / to + quick year picker) */}
        <div className="bg-white/[0.02] rounded-2xl p-4 border border-white/10 mb-6 flex flex-col md:flex-row items-stretch md:items-center gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Calendar className="h-4 w-4 text-emerald-400" />
            <span className="text-xs text-white/60">{t('السنة السريعة:', 'Quick year:')}</span>
            {YEAR_OPTIONS.map((y) => (
              <button
                key={y}
                onClick={() => applyYear(y)}
                className={
                  'px-3 py-1 rounded-full text-[11px] font-semibold border transition-colors ' +
                  (year === y
                    ? 'bg-emerald-500 text-white border-emerald-500'
                    : 'bg-[#0a0a0a] text-[#71717A] border-white/10 hover:bg-white/5')
                }
                data-testid={'year-' + y}
              >
                {y === 'all' ? t('الكل', 'All') : y}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap md:ms-auto">
            <label className="text-xs text-white/60">{t('من:', 'From:')}</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setYear('all'); }}
              className="bg-[#0a0a0a] border border-[#2a2a4e] text-white rounded-lg px-2 py-1 text-xs"
              data-testid="date-from"
            />
            <label className="text-xs text-white/60">{t('إلى:', 'To:')}</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setYear('all'); }}
              className="bg-[#0a0a0a] border border-[#2a2a4e] text-white rounded-lg px-2 py-1 text-xs"
              data-testid="date-to"
            />
            {(dateFrom || dateTo) && (
              <button
                onClick={() => { setDateFrom(''); setDateTo(''); setYear('all'); }}
                className="text-[11px] text-red-300 hover:text-red-200 underline"
                data-testid="date-clear"
              >
                {t('مسح', 'Clear')}
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center text-[#71717A]">
            {t('لا توجد طلبات', 'No requests')}
          </div>
        ) : (
          <>
            <div className="text-xs text-[#71717A] mb-3">
              {t('عدد الطلبات', 'Total')}: {total}
            </div>
            <div className="space-y-3">
              {items.map((it) => (
                <button
                  key={it.id}
                  onClick={() => navigate('/manage/cloudgm-agent/company-registration/track/' + it.id)}
                  className="w-full text-start rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] p-4 transition-colors"
                  data-testid={'row-' + it.id}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <Hash className="h-3.5 w-3.5 text-emerald-400" />
                        <span className="text-white font-mono font-bold" dir="ltr">{it.request_code}</span>
                        <span
                          className={
                            'text-[10px] px-2 py-0.5 rounded-full font-semibold ' +
                            (it.status === 'completed'
                              ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                              : 'bg-amber-500/15 text-amber-300 border border-amber-500/30')
                          }
                        >
                          {it.status === 'completed' ? t('مكتمل', 'Completed') : t('قيد الإجراء', 'In progress')}
                        </span>
                        {/* iter-261 — request_date badge front-and-centre on the top card */}
                        {it.request_date && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-mono bg-cyan-500/15 text-cyan-200 border border-cyan-500/30"
                                data-testid={'row-date-' + it.id}>
                            <Calendar className="h-2.5 w-2.5" />
                            {t('تاريخ الطلب:', 'Request Date:')} {fmtDate(it.request_date, isAr)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-white">
                        <User className="h-3.5 w-3.5 text-amber-400" />
                        {it.client?.name || '—'}
                        {it.client?.phone ? <span className="text-xs text-[#71717A]" dir="ltr">· {it.client.phone}</span> : null}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-[#71717A] mt-1">
                        <Building2 className="h-3 w-3 text-cyan-400" />
                        {isAr ? (it.authority_name_ar || it.authority_name_en) : (it.authority_name_en || it.authority_name_ar)}
                        {' — '}
                        {isAr ? (it.region_name_ar || it.region_name_en) : (it.region_name_en || it.region_name_ar)}
                      </div>
                    </div>
                    <div className="text-xs text-[#606060] whitespace-nowrap text-end">
                      <div>{t('أُنشئ:', 'Created:')} {it.created_at ? new Date(it.created_at).toLocaleString(isAr ? 'ar-EG' : 'en-GB') : ''}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </AgentPageShell>
  );
}
